import { useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Modal, Textarea } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";

interface RejectFormValues {
  rejection_reason: string;
}

interface Props {
  applicationId: string;
  onRejected: () => void | Promise<void>;
  onClose: () => void;
}

const REASON_MIN = 10;
const REASON_MAX = 1000;

/**
 * Modal for rejecting a trade-account application.
 *
 * Requires a meaningful `rejection_reason` (10–1000 chars) so the
 * applicant can understand the outcome and resubmit with corrected info.
 * Posts to `POST /v1/admin/applications/:id/reject` with an
 * Idempotency-Key (added automatically by api-client).
 *
 * On 422 the field-level error is rendered inline. On success the
 * caller's `onRejected()` callback is invoked (caller closes + navigates
 * + toasts).
 */
export function RejectApplicationModal({
  applicationId,
  onRejected,
  onClose,
}: Props) {
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { isSubmitting, errors },
  } = useForm<RejectFormValues>({
    defaultValues: { rejection_reason: "" },
  });

  const reason = watch("rejection_reason");

  async function onSubmit(values: RejectFormValues) {
    setServerError(null);
    try {
      await api.post(`/v1/admin/applications/${applicationId}/reject`, {
        rejection_reason: values.rejection_reason,
      });
      await onRejected();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422) {
          setError("rejection_reason", {
            message: err.problem?.title ?? "Reason is too short or invalid.",
          });
        } else if (err.problem) {
          setServerError(err.problem.title ?? "Reject failed");
        } else {
          setServerError("Reject failed. Please try again.");
        }
      } else {
        setServerError("Reject failed. Please try again.");
      }
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Reject application"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Reject
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <Textarea
          label="Rejection reason"
          rows={4}
          maxLength={REASON_MAX}
          helper={`${reason.length}/${REASON_MAX} — Helpful: explain what they need to fix to resubmit, or why this is a permanent decline.`}
          error={errors.rejection_reason?.message}
          {...register("rejection_reason", {
            required: "Rejection reason is required",
            minLength: {
              value: REASON_MIN,
              message: `Reason must be at least ${REASON_MIN} characters`,
            },
            maxLength: {
              value: REASON_MAX,
              message: `Reason must be at most ${REASON_MAX} characters`,
            },
          })}
        />

        {serverError ? (
          <p role="alert" className="rounded-sm bg-danger-soft px-3 py-2 text-body-sm text-danger">
            {serverError}
          </p>
        ) : null}

        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
